import { useRef, useCallback, useState, useMemo } from 'react';
import { CanvasElement, AspectRatio, ASPECT_CSS, GlobalSettings } from '@/lib/engineTypes';
import type { Conflict } from '@/lib/conflictEngine';
import { Sun, Camera, User, Minus, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

interface Props {
  elements: CanvasElement[];
  aspectRatio: AspectRatio;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onMove: (id: string, x: number, y: number) => void;
  onScale: (id: string, scale: number) => void;
  globalSettings: GlobalSettings;
  magneticGuides?: { x: number | null; y: number | null };
  conflicts?: Conflict[];
}

const iconMap = { light: Sun, camera: Camera, character: User };

const graphMetaNodes = [
  { id: 'scene', x: 12, y: 14, label: 'Scene' },
  { id: 'composition', x: 86, y: 14, label: 'Composition' },
];

const typeStyles = {
  light: {
    normal: 'border-amber-400/40 bg-amber-400/8 text-amber-400',
    selected: 'border-amber-400/80 bg-amber-400/20 text-amber-400',
    glow: '0 0 20px hsl(45 90% 65% / 0.4), 0 0 50px hsl(45 90% 65% / 0.15)',
    line: 'hsl(45 90% 65% / 0.15)',
  },
  camera: {
    normal: 'border-sky-400/40 bg-sky-400/8 text-sky-400',
    selected: 'border-sky-400/80 bg-sky-400/20 text-sky-400',
    glow: '0 0 20px hsl(210 80% 60% / 0.4), 0 0 50px hsl(210 80% 60% / 0.15)',
    line: 'hsl(210 80% 60% / 0.15)',
  },
  character: {
    normal: 'border-rose-400/40 bg-rose-400/8 text-rose-400',
    selected: 'border-rose-400/80 bg-rose-400/20 text-rose-400',
    glow: '0 0 20px hsl(340 70% 60% / 0.4), 0 0 50px hsl(340 70% 60% / 0.15)',
    line: 'hsl(340 70% 60% / 0.15)',
  },
};

export default function VisualCanvas({ elements, aspectRatio, selectedId, onSelect, onMove, onScale, globalSettings, magneticGuides, conflicts = [] }: Props) {
  const { t } = useLanguage();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const justSelectedRef = useRef(false);

  const handlePointerDown = useCallback((id: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    justSelectedRef.current = true;
    setDragging(id);
    onSelect(id);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [onSelect]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onMove(dragging, x, y);
  }, [dragging, onMove]);

  const handlePointerUp = useCallback(() => { setDragging(null); }, []);

  const handleCanvasClick = useCallback(() => {
    // Don't deselect if we just selected an element via pointerDown
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    onSelect(null);
  }, [onSelect]);

  const handleWheel = useCallback((id: string, e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = elements.find(el => el.id === id);
    if (!el) return;
    onScale(id, el.scale + (e.deltaY > 0 ? -0.1 : 0.1));
  }, [elements, onScale]);

  // Connection lines between elements
  const connections = useMemo(() => {
    const cameras = elements.filter(e => e.type === 'camera');
    const characters = elements.filter(e => e.type === 'character');
    const lights = elements.filter(e => e.type === 'light');
    const lines: { x1: number; y1: number; x2: number; y2: number; color: string }[] = [];
    cameras.forEach(cam => characters.forEach(ch => {
      lines.push({ x1: cam.x, y1: cam.y, x2: ch.x, y2: ch.y, color: typeStyles.camera.line });
    }));
    lights.forEach(l => characters.forEach(ch => {
      lines.push({ x1: l.x, y1: l.y, x2: ch.x, y2: ch.y, color: typeStyles.light.line });
    }));
    return lines;
  }, [elements]);

  const conflictConnections = useMemo(() => {
    const lines: { x1: number; y1: number; x2: number; y2: number; label: string }[] = [];
    const camera = elements.find((e) => e.type === 'camera');
    const light = elements.find((e) => e.type === 'light');
    const character = elements.find((e) => e.type === 'character');

    conflicts.forEach((conflict) => {
      const lower = `${conflict.id} ${conflict.message}`.toLowerCase();
      if ((lower.includes('light') || lower.includes('lighting')) && light && camera) {
        lines.push({ x1: light.x, y1: light.y, x2: camera.x, y2: camera.y, label: 'Lighting vs Camera' });
      }

      if ((lower.includes('camera') || lower.includes('composition') || lower.includes('lens')) && camera && character) {
        lines.push({ x1: camera.x, y1: camera.y, x2: character.x, y2: character.y, label: 'Camera vs Composition' });
      }
    });

    return lines;
  }, [conflicts, elements]);


  return (
    <div className="flex items-center justify-center flex-1 p-2 md:p-3 min-h-0 overflow-hidden">
      <div
        ref={canvasRef}
        className={`canvas-shell relative ${ASPECT_CSS[aspectRatio] || 'aspect-video'} max-h-full w-full max-w-[1040px] rounded-2xl border border-border/30 bg-canvas-bg canvas-grid overflow-hidden canvas-ambient`}
        style={globalSettings.horizonTilt !== 0 ? { transform: `rotate(${globalSettings.horizonTilt}deg)` } : undefined}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onClick={handleCanvasClick}
      >
        {/* Ambient radial glows */}
        <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-radial from-primary/[0.03] to-transparent pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-radial from-primary/[0.03] to-transparent pointer-events-none" />

        {/* Center crosshair */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-primary/[0.06]" />
        <div className="absolute top-1/2 left-0 right-0 h-px bg-primary/[0.06]" />

        {/* Rule of thirds */}
        {globalSettings.ruleOfThirds && (
          <>
            <div className="absolute left-[33.33%] top-0 bottom-0 w-px bg-primary/[0.04]" />
            <div className="absolute left-[66.66%] top-0 bottom-0 w-px bg-primary/[0.04]" />
            <div className="absolute top-[33.33%] left-0 right-0 h-px bg-primary/[0.04]" />
            <div className="absolute top-[66.66%] left-0 right-0 h-px bg-primary/[0.04]" />
            {[33.33, 66.66].map(x => [33.33, 66.66].map(y => (
              <div key={`${x}-${y}`} className="absolute w-1 h-1 rounded-full bg-primary/10" style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%,-50%)' }} />
            )))}
          </>
        )}

        {/* Symmetry line */}
        {globalSettings.symmetryMode && (
          <div className="absolute left-1/2 top-0 bottom-0 w-px border-l border-dashed border-primary/15" />
        )}

        {/* Magnetic guides */}
        {magneticGuides?.x != null && (
          <div className="absolute top-0 bottom-0 w-px bg-primary/25 pointer-events-none transition-opacity" style={{ left: `${magneticGuides.x}%` }} />
        )}
        {magneticGuides?.y != null && (
          <div className="absolute left-0 right-0 h-px bg-primary/25 pointer-events-none transition-opacity" style={{ top: `${magneticGuides.y}%` }} />
        )}

        {/* Connection lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
          {connections.map((line, i) => (
            <line key={i} x1={`${line.x1}%`} y1={`${line.y1}%`} x2={`${line.x2}%`} y2={`${line.y2}%`}
              stroke={line.color} strokeWidth="1" strokeDasharray="6 4" />
          ))}
          {conflictConnections.map((line, i) => (
            <line
              key={`conflict-${i}`}
              x1={`${line.x1}%`}
              y1={`${line.y1}%`}
              x2={`${line.x2}%`}
              y2={`${line.y2}%`}
              stroke="rgba(239,68,68,0.95)"
              strokeWidth="2"
              strokeDasharray="4 3"
            />
          ))}
        </svg>

        {/* Graph label */}
        <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-md text-[8px] font-mono bg-background/50 text-primary/70 backdrop-blur-sm border border-primary/20 select-none">
          {t('canvas.promptGraph')}
        </div>

        {/* Aspect label */}
        <div className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-md text-[8px] font-mono bg-background/50 text-muted-foreground/40 backdrop-blur-sm border border-border/20 select-none">
          {aspectRatio}
        </div>

        {[
          { id: 'scene', x: 12, y: 14, label: t('canvas.scene') },
          { id: 'composition', x: 86, y: 14, label: t('canvas.composition') },
        ].map((node) => (
          <div
            key={node.id}
            className="absolute px-2 py-1 rounded-md border border-primary/35 bg-primary/10 text-[8px] font-mono text-primary/80"
            style={{ left: `${node.x}%`, top: `${node.y}%`, transform: 'translate(-50%, -50%)' }}
          >
            {node.label}
          </div>
        ))}

        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
          <line x1="12%" y1="14%" x2="50%" y2="50%" stroke="rgba(34,197,94,0.35)" strokeWidth="1" strokeDasharray="3 3" />
          <line x1="86%" y1="14%" x2="50%" y2="50%" stroke="rgba(56,189,248,0.35)" strokeWidth="1" strokeDasharray="3 3" />
        </svg>

        {/* Empty state */}
        {elements.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-muted/15 border border-dashed border-border/20 flex items-center justify-center mx-auto">
                <Sun className="w-6 h-6 text-muted-foreground/15" />
              </div>
              <p className="text-[11px] font-mono text-muted-foreground/20">
                {t('canvas.addElements')}
              </p>
            </div>
          </div>
        )}

        {/* Elements */}
        {elements.map(el => {
          const Icon = iconMap[el.type];
          const isSelected = el.id === selectedId;
          const styles = typeStyles[el.type];
          const size = 42 * el.scale;

          return (
            <motion.div
              key={el.id}
              className="absolute flex flex-col items-center select-none transition-transform duration-150"
              style={{
                left: `${el.x}%`,
                top: `${el.y}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: isSelected ? 10 : 1,
                cursor: dragging === el.id ? 'grabbing' : 'grab',
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              onPointerDown={(e) => handlePointerDown(el.id, e)}
              onClick={(e) => { e.stopPropagation(); onSelect(el.id); }}
              onWheel={(e) => handleWheel(el.id, e)}
            >
              {/* Selection ring */}
              {isSelected && (
                <motion.div
                  className="absolute rounded-full border-2 border-primary/30"
                  style={{ width: `${size + 16}px`, height: `${size + 16}px` }}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                />
              )}

              {/* Main node */}
              <motion.div
                className={`rounded-full border-2 flex items-center justify-center transition-all duration-150 hover:brightness-110 ${
                  isSelected ? styles.selected : styles.normal
                }`}
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  boxShadow: isSelected ? styles.glow : 'none',
                }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon style={{ width: `${size * 0.4}px`, height: `${size * 0.4}px` }} />
              </motion.div>

              {/* Label */}
              <span className={`mt-1.5 text-[9px] font-mono whitespace-nowrap transition-colors ${
                isSelected ? 'text-foreground/90' : 'text-muted-foreground/40'
              }`}>
                {el.label}
              </span>

              {/* Position coords when selected */}
              {isSelected && (
                <span className="text-[7px] font-mono text-primary/50 tabular-nums">
                  {Math.round(el.x)}, {Math.round(el.y)}
                </span>
              )}

              {/* Scale controls when selected */}
              {isSelected && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-1.5 mt-0.5"
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); justSelectedRef.current = true; onScale(el.id, el.scale - 0.2); }}
                    className="w-5 h-5 rounded-full bg-background/90 border border-border/50 flex items-center justify-center hover:border-primary/50 transition-colors backdrop-blur-sm"
                  >
                    <Minus className="w-2.5 h-2.5 text-muted-foreground" />
                  </button>
                  <span className="text-[8px] font-mono text-primary/70 tabular-nums min-w-[24px] text-center">{el.scale.toFixed(1)}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); justSelectedRef.current = true; onScale(el.id, el.scale + 0.2); }}
                    className="w-5 h-5 rounded-full bg-background/90 border border-border/50 flex items-center justify-center hover:border-primary/50 transition-colors backdrop-blur-sm"
                  >
                    <Plus className="w-2.5 h-2.5 text-muted-foreground" />
                  </button>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
